import { computed, inject, Injectable } from '@angular/core';
import { CookieApiService } from '@nebalkon-workspace/browser-api';
import { CookieKeyEnum } from '@nebalkon-workspace/common';
import { BaseStateService } from '@nebalkon-workspace/base-state';

/**
 * Enumeration of application interface types.
 */
export enum AppTypeEnum {
  /** Web application interface */
  Web = 'web',
  /** Mobile application interface */
  Mobile = 'mobile',
}

/**
 * Service for managing and detecting the application interface type (web or mobile).
 * 
 * Extends BaseStateService to provide reactive state management for the current app interface.
 * The interface type is persisted in cookies and can be used to conditionally render UI elements.
 * 
 * @example
 * ```typescript
 * // In a component
 * readonly appInterface = inject(AppInterfaceService);
 * 
 * ngOnInit() {
 *   this.appInterface.defineAppInterface();
 *   
 *   if (this.appInterface.isWeb()) {
 *     // Show web-specific UI
 *   }
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class AppInterfaceService extends BaseStateService<AppTypeEnum> {
  readonly #cookieService = inject(CookieApiService);

  /**
   * Computed signal that returns true if the current interface is web.
   */
  public readonly isWeb = computed(() => this.value() === AppTypeEnum.Web);
  
  /**
   * Computed signal that returns true if the current interface is mobile.
   */
  public readonly isMobile = computed(() => this.value() === AppTypeEnum.Mobile);

  /**
   * Determines and sets the application interface type from cookies.
   * Defaults to web interface if no cookie is found.
   */
  public defineAppInterface(): void {
    const appInterface =
      (this.#cookieService.getItem(CookieKeyEnum.AppType) as AppTypeEnum) ||
      AppTypeEnum.Web;

    this.setValue(appInterface);
  }
}
